// services/AuthService.ts
import type { ApiResponse } from "@/types/common";
import { privateRequest, publicRequest } from "../axios";
import { type AxiosResponse } from "axios";

// Define the request/response types
export interface LoginRequest {
  email: string;
  otp: string;
}

export type OtpPurpose = "EMAIL_VERIFICATION";

export interface GenerateOtpRequest {
  email: string;
  purpose: OtpPurpose;
}

export interface BackendPermission {
  id: number;
  actionName: string;
  description: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  permissions : string[];
}

export interface SignUpRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobileNo: string;
  location: string;
  roleIds: number[];
  csrp?: string;
  companyName?: string;
  companyWebsite?: string;
  dob: string;
  gender: string;
  userType: string;
  designation?: string;
  socialLinks?: {
    instagramProfileUrl?: string;
    linkedinProfileUrl?: string;
  };
  socialProvider?: string;
  researchInterests?: string[];
  isPushNotificationEnabled?: boolean;
  language?: string;
}

export interface SignUpResponse {
  id?: string | number;
  email?: string;
  firstName?: string;
  lastName?: string;
  message?: string;
  accessToken?: string;
  refreshToken?: string;
  permissions?: string[];
  userType?: string;
}

interface ForgotPasswordRequest {
  email: string;
}

interface ResetPasswordRequest {
  token: string;
  password: string;
  email: string;
  ResetPasswordMethod : string;
}

const ENDPOINTS = {
  LOGIN: "/auth/login/otp/validate",
  GENERATE_OTP: "/auth/generate-otp",
  SIGNUP: "/auth/signup",
  LOGOUT: "/auth/logout",
  FORGOT_PASSWORD: "/auth/forgot-password",
  RESET_PASSWORD: "/auth/reset-password",
};

const login = async (data: LoginRequest) => {
  const response = await publicRequest.post<ApiResponse<LoginResponse>>(
    ENDPOINTS.LOGIN,
    data,
  );
  return response?.data;
};

const generateOtp = async (data: GenerateOtpRequest) => {
  const response = await publicRequest.post<ApiResponse<unknown>>(
    ENDPOINTS.GENERATE_OTP,
    data,
  );
  return response?.data;
};

const signup = async (data: SignUpRequest) => {
  const response = await publicRequest.post<ApiResponse<SignUpResponse>>(
    ENDPOINTS.SIGNUP,
    data,
  );
  return response?.data;
};

const logout = async (): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> =
    await privateRequest.post(ENDPOINTS.LOGOUT);
  return response.data;
};

const forgotPassword = async (
  data: ForgotPasswordRequest,
): Promise<{ message: string }> => {
  try {
    const response: AxiosResponse<{ message: string }> =
      await publicRequest.post(ENDPOINTS.FORGOT_PASSWORD, data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

const reset = async (
  data: ResetPasswordRequest,
): Promise<{ message: string }> => {
  try {
    const response: AxiosResponse<any> =
      await publicRequest.post(ENDPOINTS.RESET_PASSWORD, data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

const AuthService = {
  login,
  generateOtp,
  signup,
  logout,
  forgotPassword,
  reset,
};

export default AuthService;
